/**
 * Planner Agent - Tools Section
 * 
 * Standard tools section with preamble and postamble from Keywords AI.
 */

import { PromptSectionBase } from '../../base/systemPrompt/PromptSectionBase';
import type { ToolBase } from '../../base/toolBase';
import { PLANNER_PROMPT_KEYS } from './variables';

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS PREAMBLE
// ═══════════════════════════════════════════════════════════════════════════

class PlannerToolsPreambleSection extends PromptSectionBase {
    protected readonly keywordsKey = PLANNER_PROMPT_KEYS.toolsPreamble;
    protected readonly fallbackContent = `## Available Tools

You have access to four tools for creating and refining presentation plans:
- **userInterview** - Ask clarifying questions when needed
- **alphaSenseSearchAgent** - Research financial and business data
- **createPlan** - Create a new presentation outline
- **editPlan** - Modify existing plans`;
}

const toolsPreambleSection = new PlannerToolsPreambleSection();

export function getToolsPreamble(): string {
    return toolsPreambleSection.getContent();
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS POSTAMBLE
// ═══════════════════════════════════════════════════════════════════════════

class PlannerToolsPostambleSection extends PromptSectionBase {
    protected readonly keywordsKey = PLANNER_PROMPT_KEYS.toolsPostamble;
    protected readonly fallbackContent = `## Tool Usage Strategy

**Phase 1: Analyze Initial Prompt**
- If vague → Call userInterview for clarification
- If business-related → Call alphaSenseSearchAgent for context (BEFORE createPlan)
- If clear and specific → Proceed directly to createPlan

**Phase 2: Research (when applicable)**
- Most business presentations benefit from alphaSenseSearchAgent
- Use research to inform section structure and slide purposes
- Research IDs (as_*) stay in your memory but NOT in Plan JSON

**Phase 3: Create Plan**
- **CRITICAL:** You MUST call createPlan tool (don't just describe the plan)
- Use research findings to inform structure
- Aim for logical section groupings

**Phase 4: Iterate**
- Listen to user feedback
- Call editPlan with specific operations
- Continue until user is satisfied`;
}

const toolsPostambleSection = new PlannerToolsPostambleSection();

export function getToolsPostamble(): string {
    return toolsPostambleSection.getContent();
}

// ═══════════════════════════════════════════════════════════════════════════
// TOOLS SECTION BUILDER
// ═══════════════════════════════════════════════════════════════════════════

export function buildToolsSection(tools: ToolBase[]): string {
    const parts: string[] = [];
    
    // 1. Preamble
    parts.push(getToolsPreamble());
    parts.push('');
    
    // 2. Individual tool descriptions
    for (const tool of tools) {
        parts.push(tool.systemPromptDescription);
        parts.push('');
    }
    
    // 3. Postamble
    parts.push(getToolsPostamble());
    
    return parts.join('\n');
}
